import { useState } from 'react';

import { RateAndReviewModal } from '../rateAndReviewModal/rateAndReviewModal';
import type { RateAndReviewModalVariantProps } from '../rateAndReviewModalVariant';
import { RateAndReviewWizard } from '../rateAndReviewWizard/rateAndReviewWizard';
import { ReviewProductHeader } from '../reviewProductHeader/reviewProductHeader';
import { ReviewSubmitFailed } from '../reviewSubmitFailed/reviewSubmitFailed';
import { ReviewSubmitted } from '../reviewSubmitted/reviewSubmitted';

export type RateAndReviewWizardModalProps = RateAndReviewModalVariantProps;

/**
 * A/B test variant B: the modal shell with one question per step.
 * Owns the current step and restarts from the first step whenever a review
 * starts: on open, and when the next product is picked from the confirmation.
 */
export function RateAndReviewWizardModal({
  activityOptions,
  brand,
  category,
  errorMessage,
  feelingOptions,
  imageUrl,
  isMobile,
  isRetryable = true,
  isSubmitting,
  moreToReview,
  onChange,
  onRequestClose,
  onReviewProduct,
  onSubmit,
  open,
  productName,
  status = 'editing',
  testId,
  title,
  value,
}: RateAndReviewWizardModalProps) {
  const [step, setStep] = useState(0);
  const isShowingForm = open && status === 'editing';
  const [wasShowingForm, setWasShowingForm] = useState(isShowingForm);

  if (isShowingForm !== wasShowingForm) {
    setWasShowingForm(isShowingForm);
    if (isShowingForm) setStep(0);
  }

  const renderBody = () => {
    if (status === 'submitted') {
      return (
        <ReviewSubmitted
          moreToReview={moreToReview}
          onDone={onRequestClose}
          onReviewProduct={onReviewProduct}
          productName={productName}
          rating={value.rating}
        />
      );
    }
    if (status === 'failed') {
      return isRetryable ? (
        <ReviewSubmitFailed
          errorMessage={errorMessage}
          isSubmitting={isSubmitting}
          onRetry={onSubmit}
        />
      ) : (
        <ReviewSubmitFailed
          errorMessage={errorMessage}
          isRetryable={false}
          onClose={onRequestClose}
        />
      );
    }
    return (
      <RateAndReviewWizard
        activityOptions={activityOptions}
        feelingOptions={feelingOptions}
        isSubmitting={isSubmitting}
        onChange={onChange}
        onStepChange={setStep}
        onSubmit={onSubmit}
        step={step}
        value={value}
      />
    );
  };

  return (
    <RateAndReviewModal
      isMobile={isMobile}
      onBack={
        step > 0 && !isSubmitting && status === 'editing'
          ? () => setStep(step - 1)
          : undefined
      }
      onRequestClose={onRequestClose}
      open={open}
      productHeader={
        status === 'editing' && (
          <ReviewProductHeader
            brand={brand}
            category={category}
            imageUrl={imageUrl}
            name={productName}
            size="compact"
          />
        )
      }
      testId={testId}
      title={title}
    >
      {renderBody()}
    </RateAndReviewModal>
  );
}
